import Modal from "@/components/common/modal";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import { Button } from "@elaniintc/goes-design-system";
import { useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";

interface EditProcessModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const InactivityModal = ({
  isOpen = false,
  onClose = () => {},
}: EditProcessModalProps) => {
  const [seconds, setSeconds] = useState(60);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { keycloak } = useAuth();

  useEffect(() => {
    /* ───────── Change in another tab ───────── */
    const onRemoteStorage = (e: StorageEvent) => {
      if (e.key === "isOpen" || e.key === "activity") {
        if (isOpen) {
          onClose();
        }
      }
    };

    /* ───────── Change in this tab ───────── */
    const onLocalStorage = (e: Event) => {
      const { detail } = e as CustomEvent<string>;
      return detail;
      // No se hace nada aquí con el modal, permanece funcionando con normalidad
    };

    globalThis.window.addEventListener("storage", onRemoteStorage);
    globalThis.window.addEventListener("isOpen-local-change", onLocalStorage);

    return () => {
      globalThis.window.removeEventListener("storage", onRemoteStorage);
      globalThis.window.removeEventListener(
        "isOpen-local-change",
        onLocalStorage,
      );
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSeconds(60);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || seconds === 0) return;

    intervalRef.current = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen, seconds]);

  useEffect(() => {
    if (seconds === 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const url = new URL(globalThis.window.location.href);
      url.searchParams.set("logout_reason", "inactivity");
      Sentry.setUser(null);
      Sentry.getReplay()?.stop();
      keycloak.logout({
        redirectUri: url.toString(),
      });
    }
  }, [seconds, keycloak]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="¿Sigues ahí?"
      description="Tu sesión está a punto de expirar. Confirma que deseas seguir utilizando la plataforma o cierra tu sesión."
      modalContentClassName="max-w-[520px]"
    >
      {/* Avoid autofocus */}
      <button type="button" className="h-0 absolute"></button>
      <div className="bg-surface-warning-accent-quiet text-warning font-light text-center rounded-lg px-2 py-4">
        La sesión cerrará en:{" "}
        <span className="font-bold">{seconds} segundos</span>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2">
        <Button
          autoFocus={false}
          type="button"
          variant="tertiary"
          className="w-full font-semibold border"
          onClick={() => {
            if (keycloak) keycloak.logout();
          }}
        >
          Cerrar sesión
        </Button>
        <Button
          className="w-full font-semibold"
          onClick={onClose}
          autoFocus={false}
        >
          Continuar
        </Button>
      </div>
    </Modal>
  );
};
